'use client';

import React, { useState } from 'react';
import { Pencil, Trash2, ArrowUpCircle, ArrowDownCircle, ChevronUp, ChevronDown, ChevronsUpDown, ClipboardList } from 'lucide-react';
import type { Transaction } from './CashFlowSheetClient';

type SortField = 'data' | 'descricao' | 'valor' | 'saldo';
type SortDir = 'asc' | 'desc';

interface TransactionTableProps {
  transactions: Transaction[];
  deletingFadeId: string | null;
  onEdit: (txn: Transaction) => void;
  onDelete: (id: string) => void;
  formatDateBR: (iso: string) => string;
  formatCurrency: (v: number) => string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  isFiltered?: boolean;
}

export default function TransactionTable({
  transactions,
  deletingFadeId,
  onEdit,
  onDelete,
  formatDateBR,
  formatCurrency,
  searchQuery,
  onSearchChange,
  isFiltered = false,
}: TransactionTableProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Filtering — determine which rows match the search
  const matchesSearch = (t: Transaction): boolean => {
    if (!searchQuery) return true;
    // Split by "+" to support multiple terms (OR logic)
    const terms = searchQuery.split('+').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (terms.length === 0) return true;
    return terms.some(q =>
      t.descricao.toLowerCase().includes(q) ||
      formatDateBR(t.data).includes(q) ||
      String(t.valor).includes(q) ||
      (t.tipoOperacao ?? '').toLowerCase().includes(q) ||
      (t.numero ?? '').toLowerCase().includes(q)
    );
  };

  // All transactions (for rendering — non-matching are hidden via CSS when search is active)
  const allRows = sortField
    ? [...transactions].sort((a, b) => {
        let av: string | number = '';
        let bv: string | number = '';
        if (sortField === 'data') { av = a.data; bv = b.data; }
        else if (sortField === 'descricao') { av = a.descricao.toLowerCase(); bv = b.descricao.toLowerCase(); }
        else if (sortField === 'valor') { av = a.valor; bv = b.valor; }
        else if (sortField === 'saldo') { av = a.saldo; bv = b.saldo; }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      })
    : transactions;

  // Count of visible (matching) rows — used for header label and empty state
  const visibleCount = searchQuery ? allRows.filter((t) => matchesSearch(t)).length : allRows.length;

  // Keep sorted alias for tfoot totals (only matching rows contribute to totals)
  const sorted = searchQuery ? allRows.filter((t) => matchesSearch(t)) : allRows;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={13} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-primary" />
      : <ChevronDown size={13} className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-card overflow-hidden">
      {/* Table toolbar */}
      <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          Lançamentos
          {searchQuery && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              — {visibleCount} resultado{visibleCount !== 1 ? 's' : ''} para &quot;{searchQuery}&quot;
            </span>
          )}
        </h2>
        <div className="relative w-full sm:w-96">
          <input
            type="text"
            placeholder="Buscar lançamentos… (use + para múltiplos termos)"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="
              w-full pl-3 pr-8 py-1.5 rounded-md border border-input bg-background
              text-sm text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring
              transition-colors duration-150
            "
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Limpar busca"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Table scroll wrapper */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-4 py-2.5 w-32">
                <button
                  onClick={() => handleSort('data')}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                >
                  Data <SortIcon field="data" />
                </button>
              </th>
              <th className="text-left px-4 py-2.5">
                <button
                  onClick={() => handleSort('descricao')}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                >
                  Descrição <SortIcon field="descricao" />
                </button>
              </th>
              <th className="text-right px-4 py-2.5 w-36">
                <button
                  onClick={() => handleSort('valor')}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors ml-auto"
                >
                  Entrada <SortIcon field="valor" />
                </button>
              </th>
              <th className="text-right px-4 py-2.5 w-36">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Saída
                </span>
              </th>
              {!isFiltered && (
                <th className="text-right px-4 py-2.5 w-36">
                  <button
                    onClick={() => handleSort('saldo')}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors ml-auto"
                  >
                    Saldo <SortIcon field="saldo" />
                  </button>
                </th>
              )}
              <th className="px-4 py-2.5 w-20">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {allRows.length === 0 ? (
              <tr>
                <td colSpan={isFiltered ? 5 : 6}>
                  <EmptyTableState hasSearch={!!searchQuery} onClearSearch={() => onSearchChange('')} />
                </td>
              </tr>
            ) : visibleCount === 0 && searchQuery ? (
              <tr>
                <td colSpan={isFiltered ? 5 : 6}>
                  <EmptyTableState hasSearch={true} onClearSearch={() => onSearchChange('')} />
                </td>
              </tr>
            ) : (
              allRows.map((txn, idx) => {
                const visible = matchesSearch(txn);
                return (
                  <TransactionRow
                    key={txn.id}
                    txn={txn}
                    idx={idx}
                    isFading={deletingFadeId === txn.id}
                    isHovered={hoveredId === txn.id}
                    hidden={!visible}
                    isFiltered={isFiltered}
                    onMouseEnter={() => setHoveredId(txn.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    formatDateBR={formatDateBR}
                    formatCurrency={formatCurrency}
                  />
                );
              })
            )}
          </tbody>
          {sorted.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/30">
                <td className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide" colSpan={2}>
                  Total geral
                </td>
                <td className="px-4 py-3 text-right font-mono-nums text-sm font-semibold text-entrada">
                  {formatCurrency(
                    sorted.reduce((s, t) => (t.tipo === 'entrada' ? s + t.valor : s), 0)
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-nums text-sm font-semibold text-saida">
                  {formatCurrency(
                    sorted.reduce((s, t) => (t.tipo === 'saida' ? s + t.valor : s), 0)
                  )}
                </td>
                {!isFiltered && (
                  <td className="px-4 py-3 text-right font-mono-nums text-sm font-bold text-foreground" colSpan={2}>
                    {(() => {
                      const last = sorted[sorted.length - 1];
                      const positive = last.saldo >= 0;
                      return (
                        <span className={positive ? 'text-saldo-positive' : 'text-saldo-negative'}>
                          {formatCurrency(last.saldo)}
                        </span>
                      );
                    })()}
                  </td>
                )}
                {isFiltered && (
                  <td className="px-4 py-3" />
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ─── Row subcomponent ─────────────────────────────────────────────────────────
interface RowProps {
  txn: Transaction;
  idx: number;
  isFading: boolean;
  isHovered: boolean;
  hidden: boolean;
  isFiltered?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onEdit: (txn: Transaction) => void;
  onDelete: (id: string) => void;
  formatDateBR: (iso: string) => string;
  formatCurrency: (v: number) => string;
}

function TransactionRow({
  txn,
  idx,
  isFading,
  isHovered,
  hidden,
  isFiltered = false,
  onMouseEnter,
  onMouseLeave,
  onEdit,
  onDelete,
  formatDateBR,
  formatCurrency,
}: RowProps) {
  const isEntrada = txn.tipo === 'entrada';
  const saldoPositive = txn.saldo >= 0;

  if (hidden) return null;

  return (
    <tr
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`
        border-b border-border last:border-b-0
        transition-all duration-200
        ${isFading ? 'opacity-0 max-h-0' : 'opacity-100'}
        ${isHovered ? 'bg-muted/50' : idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'}
      `}
    >
      {/* Data */}
      <td className="px-4 py-3 font-mono-nums text-sm text-foreground whitespace-nowrap">
        {formatDateBR(txn.data)}
      </td>

      {/* Descrição */}
      <td className="px-4 py-3 text-sm text-foreground max-w-xs">
        <div className="flex items-center gap-2">
          {isEntrada ? (
            <ArrowUpCircle size={15} className="text-entrada flex-shrink-0" />
          ) : (
            <ArrowDownCircle size={15} className="text-saida flex-shrink-0" />
          )}
          <div className="min-w-0">
            <span className="truncate block" title={[txn.tipoOperacao, txn.numero, txn.descricao].filter(Boolean).join(' ')}>
              {[txn.tipoOperacao, txn.numero, txn.descricao].filter(Boolean).join(' ')}
            </span>
          </div>
        </div>
      </td>

      {/* Entrada */}
      <td className="px-4 py-3 text-right font-mono-nums text-sm">
        {isEntrada ? (
          <span className="text-entrada font-medium">{formatCurrency(txn.valor)}</span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Saída */}
      <td className="px-4 py-3 text-right font-mono-nums text-sm">
        {!isEntrada ? (
          <span className="text-saida font-medium">{formatCurrency(txn.valor)}</span>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      {/* Saldo */}
      {!isFiltered && (
        <td className="px-4 py-3 text-right font-mono-nums text-sm font-semibold">
          <span className={saldoPositive ? 'text-saldo-positive' : 'text-saldo-negative'}>
            {formatCurrency(txn.saldo)}
          </span>
        </td>
      )}

      {/* Actions */}
      <td className="px-4 py-3">
        <div
          className={`flex items-center justify-end gap-1 transition-opacity duration-150 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={() => onEdit(txn)}
            title="Editar lançamento"
            aria-label="Editar lançamento"
            className="
              p-1.5 rounded-md text-muted-foreground
              hover:text-primary hover:bg-secondary
              transition-all duration-150 active:scale-90
            "
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => onDelete(txn.id)}
            title="Excluir lançamento — esta ação não pode ser desfeita"
            aria-label="Excluir lançamento"
            className="
              p-1.5 rounded-md text-muted-foreground
              hover:text-danger hover:bg-danger/10
              transition-all duration-150 active:scale-90
            "
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyTableState({
  hasSearch,
  onClearSearch,
}: {
  hasSearch: boolean;
  onClearSearch: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mb-4">
        <ClipboardList size={28} className="text-muted-foreground" />
      </div>
      {hasSearch ? (
        <>
          <p className="text-base font-semibold text-foreground">Nenhum lançamento encontrado</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Nenhum lançamento corresponde à sua busca. Tente outros termos.
          </p>
          <button
            onClick={onClearSearch}
            className="mt-4 text-sm font-medium text-primary hover:underline"
          >
            Limpar busca
          </button>
        </>
      ) : (
        <>
          <p className="text-base font-semibold text-foreground">Nenhum lançamento ainda</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Esta folha de caixa está vazia. Clique em &quot;Novo Lançamento&quot; para registrar a primeira movimentação.
          </p>
        </>
      )}
    </div>
  );
}